import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import './Auth.css';

function Login() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const { t, language, toggleLanguage } = useLanguage();
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // Forgot Password State
    const [showForgotModal, setShowForgotModal] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetLoading, setResetLoading] = useState(false);
    const [resetMessage, setResetMessage] = useState('');
    const [resetError, setResetError] = useState('');

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const result = await login(formData.email, formData.password);
            if (result.success) {
                navigate('/dashboard');
            } else {
                setError(result.message || t('loginFailed'));
            }
        } catch (err) {
            setError(t('loginFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setResetLoading(true);
        setResetError('');
        setResetMessage('');

        try {
            const response = await fetch('/api/v1/helpdesk/client/forgot-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: resetEmail })
            });

            const data = await response.json();

            if (data.success) {
                setResetMessage(language === 'ar' 
                    ? 'تم إرسال تعليمات إعادة تعيين كلمة المرور إلى بريدك الإلكتروني.'
                    : 'Password reset instructions have been sent to your email.');
                setResetEmail('');
            } else {
                setResetError(data.message || (language === 'ar' 
                    ? 'فشل في معالجة الطلب. حاول مرة أخرى.'
                    : 'Failed to process request. Please try again.'));
            }
        } catch (err) {
            setResetError(language === 'ar' 
                ? 'خطأ في الاتصال. حاول مرة أخرى.'
                : 'Connection error. Please try again.');
        } finally {
            setResetLoading(false);
        }
    };

    const closeForgotModal = () => {
        setShowForgotModal(false);
        setResetEmail('');
        setResetMessage('');
        setResetError('');
    };

    return (
        <div className={`auth-page ${language === 'ar' ? 'rtl' : 'ltr'}`}>
            <div className="auth-container">
                <button onClick={toggleLanguage} className="lang-toggle">
                    🌐 {language === 'ar' ? 'English' : 'العربية'}
                </button>
                
                <div className="auth-logo">
                    <img src="/logo.png" alt="Matix ERP" />
                </div>
                
                <h1>Matix Helpdesk</h1>
                <p className="auth-subtitle">{t('signInToAccount')}</p>

                {error && <div className="auth-error">{error}</div>}

                <form onSubmit={handleSubmit} className="auth-form">
                    <div className="form-group">
                        <label>{t('emailAddress')}</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder={language === 'ar' ? 'أدخل بريدك الإلكتروني' : 'Enter your email'}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>{t('password')}</label>
                        <div className="password-input">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                placeholder={language === 'ar' ? 'أدخل كلمة المرور' : 'Enter your password'}
                                required
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? (language === 'ar' ? 'إخفاء' : 'Hide') : (language === 'ar' ? 'إظهار' : 'Show')}
                            </button>
                        </div>
                    </div>

                    {/* Forgot Password Link */}
                    <div className="forgot-password-wrapper">
                        <button 
                            type="button" 
                            className="forgot-password-link"
                            onClick={() => setShowForgotModal(true)}
                        >
                            {language === 'ar' ? '🔐 نسيت كلمة المرور؟' : '🔐 Forgot your password?'}
                        </button>
                    </div>

                    <button type="submit" className="auth-submit" disabled={loading}>
                        {loading ? (language === 'ar' ? 'جاري التحميل...' : 'Loading...') : t('signIn')}
                    </button>
                </form>

                <div className="auth-divider">
                    <span>{t('noAccount')}</span>
                </div>

                <Link to="/register" className="auth-link-btn">
                    {t('createAccount')}
                </Link>

                <p className="auth-footer">© 2024 MatixERP. All rights reserved.</p>
            </div>

            {/* Forgot Password Modal */}
            {showForgotModal && (
                <div className="modal-overlay" onClick={closeForgotModal}>
                    <div className={`modal-content ${language === 'ar' ? 'rtl' : 'ltr'}`} onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={closeForgotModal}>✕</button>
                        
                        <div className="modal-header">
                            <div className="modal-icon">🔐</div>
                            <h2>{language === 'ar' ? 'إعادة تعيين كلمة المرور' : 'Reset Password'}</h2>
                            <p>
                                {language === 'ar' 
                                    ? 'أدخل بريدك الإلكتروني وسنرسل لك تعليمات إعادة تعيين كلمة المرور.'
                                    : 'Enter your email address and we\'ll send you instructions to reset your password.'}
                            </p>
                        </div>

                        {resetMessage && (
                            <div className="modal-success">
                                ✓ {resetMessage}
                            </div>
                        )}

                        {resetError && (
                            <div className="modal-error">
                                ✕ {resetError}
                            </div>
                        )}

                        <form onSubmit={handleForgotPassword} className="modal-form">
                            <div className="form-group">
                                <label>{language === 'ar' ? 'البريد الإلكتروني' : 'Email Address'}</label>
                                <input
                                    type="email"
                                    value={resetEmail}
                                    onChange={(e) => setResetEmail(e.target.value)}
                                    placeholder={language === 'ar' ? 'أدخل بريدك الإلكتروني' : 'Enter your email address'}
                                    required
                                    disabled={resetLoading}
                                />
                            </div>

                            <button 
                                type="submit" 
                                className="auth-submit"
                                disabled={resetLoading || !resetEmail}
                            >
                                {resetLoading 
                                    ? (language === 'ar' ? 'جاري الإرسال...' : 'Sending...') 
                                    : (language === 'ar' ? '📧 إرسال تعليمات الإعادة' : '📧 Send Reset Instructions')}
                            </button>
                        </form>

                        <button className="modal-back-btn" onClick={closeForgotModal}>
                            {language === 'ar' ? '← العودة لتسجيل الدخول' : '← Back to Login'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Login;